/**
 * Small shared pieces for the Hub screens (design: tasks-hubs.jsx,
 * tasks-wizard.jsx, tasks-squads.jsx): icon tiles, avatars, the ⋯ menu,
 * the dialog/panel shells and the type-to-confirm delete.
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Briefcase,
  Code,
  Flag,
  Folder,
  Globe,
  Hash,
  Megaphone,
  MoreHorizontal,
  Palette,
  Rocket,
  Sparkles,
  Target,
  TriangleAlert,
  Users,
  Wrench,
  X,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@weco/ui";
import { Dropdown } from "../ui";

const ICONS: Record<string, LucideIcon> = {
  Folder, Rocket, Palette, Code, Megaphone, Users, Briefcase, Sparkles, Target, Flag, Wrench, Globe, Hash,
};

export function HubIconGlyph({ name, className }: { name: string; className?: string }) {
  const Icon = ICONS[name] ?? Folder;
  return <Icon className={className} aria-hidden />;
}

/** The design's tinted tile: the hub colour at low alpha behind its icon. */
export function tileStyle(hex: string): CSSProperties {
  return { backgroundColor: `${hex}33`, color: hex };
}

export function HubTile({ icon, color, size = 16, className }: { icon: string; color: string; size?: number; className?: string }) {
  return (
    <span
      className={cn("rounded flex items-center justify-center shrink-0", className)}
      style={{ ...tileStyle(color), width: size, height: size }}
    >
      <HubIconGlyph name={icon} className="size-[62%]" />
    </span>
  );
}

export function SquadTile({ color, size = 32, className }: { color: string; size?: number; className?: string }) {
  return (
    <span className={cn("rounded-lg flex items-center justify-center shrink-0", className)} style={{ ...tileStyle(color), width: size, height: size }}>
      <Hash className="size-[48%]" aria-hidden />
    </span>
  );
}

export function initialsOf(name: string): string {
  const parts = (name || "?").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "?") + (parts[1]?.[0] ?? "")).toUpperCase();
}

export function PersonAvatar({ name, size = 24, className }: { name: string; size?: number; className?: string }) {
  return (
    <span
      title={name}
      className={cn("rounded-full bg-muted text-foreground/80 font-bold flex items-center justify-center shrink-0 border-2 border-background", className)}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}
    >
      {initialsOf(name)}
    </span>
  );
}

export function AvatarStack({ people, max = 3, size = 22 }: { people: { name: string; email: string }[]; max?: number; size?: number }) {
  const shown = people.slice(0, max);
  const rest = people.length - shown.length;
  return (
    <span className="flex items-center -space-x-2 rtl:space-x-reverse">
      {shown.map((p) => <PersonAvatar key={p.email} name={p.name || p.email} size={size} />)}
      {rest > 0 && (
        <span className="rounded-full border-2 border-background bg-muted flex items-center justify-center text-[10px] font-semibold" style={{ width: size, height: size }}>
          +{rest}
        </span>
      )}
    </span>
  );
}

export interface MenuEntry {
  label: string;
  icon: LucideIcon;
  onSelect: () => void;
  danger?: boolean;
  hidden?: boolean;
}

/** A ⋯ trigger and its menu (design: SqMenu). `entries` may contain "divider". */
export function ActionMenu({
  label,
  entries,
  triggerClassName,
  iconSize = 15,
  onOpenChange,
  align = "end",
  note,
}: {
  label: string;
  entries: (MenuEntry | "divider")[];
  triggerClassName?: string;
  iconSize?: number;
  onOpenChange?: (open: boolean) => void;
  align?: "start" | "end";
  /** A muted line under the items (e.g. why some actions are missing). */
  note?: string;
}) {
  const [open, setOpen] = useState(false);
  const set = (v: boolean) => {
    setOpen(v);
    onOpenChange?.(v);
  };
  const visible = entries.filter((e) => e === "divider" || !e.hidden);
  // Drop dividers that would sit at an edge or next to another.
  const items = visible.filter((e, i) => e !== "divider" || (i > 0 && i < visible.length - 1 && visible[i - 1] !== "divider"));
  if (!items.some((e) => e !== "divider")) return null;
  return (
    <span className="relative inline-flex" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => set(!open)}
        className={cn(
          "shrink-0 inline-flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-foreground/[0.06] transition-colors",
          open && "bg-foreground/[0.06] text-foreground",
          triggerClassName,
        )}
      >
        <MoreHorizontal style={{ width: iconSize, height: iconSize }} />
      </button>
      <Dropdown open={open} onClose={() => set(false)} align={align} className="w-56 p-1">
        <div role="menu" aria-label={label}>
          {items.map((it, i) =>
            it === "divider" ? (
              <div key={`d${i}`} role="separator" className="my-1 h-px bg-border" />
            ) : (
              <button
                key={it.label}
                type="button"
                role="menuitem"
                onClick={() => {
                  set(false);
                  it.onSelect();
                }}
                className={cn(
                  "w-full flex items-center gap-2.5 px-3 h-9 text-sm font-medium rounded-xl text-start transition-colors",
                  it.danger ? "text-red-500 hover:bg-red-500/10" : "text-foreground hover:bg-muted",
                )}
              >
                <it.icon className={cn("size-[15px]", !it.danger && "text-muted-foreground")} />
                <span className="flex-1 truncate">{it.label}</span>
              </button>
            ),
          )}
          {note && <p className="px-3 pt-2 pb-1.5 mt-1 border-t border-border text-xs text-muted-foreground leading-snug">{note}</p>}
        </div>
      </Dropdown>
    </span>
  );
}

/** Centred dialog shell (design: wizard/EntityFormModal chrome). */
export function Modal({
  children,
  onClose,
  labelledBy,
  className,
  z = "z-[60]",
}: {
  children: ReactNode;
  onClose: () => void;
  labelledBy: string;
  className?: string;
  z?: string;
}) {
  return (
    <div className={cn("fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4", z)} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={cn("w-full bg-background border border-border rounded-3xl shadow-2xl flex flex-col anim-scale-in overflow-hidden max-h-[90vh]", className)}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

export function CloseButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="w-9 h-9 shrink-0 flex items-center justify-center bg-foreground/[0.06] hover:bg-foreground/[0.12] rounded-full text-muted-foreground hover:text-foreground transition-colors"
    >
      <X className="size-[17px]" />
    </button>
  );
}

/** US 1.5 delete: states what goes and requires the Hub name typed in. */
export function TypedDeleteDialog({
  hubName,
  squads,
  tasks,
  busy,
  onConfirm,
  onClose,
}: {
  hubName: string;
  squads: number;
  tasks: number;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => inputRef.current?.focus(), []);
  const ok = typed.trim() === hubName;
  return (
    <Modal onClose={onClose} labelledBy="hub-delete-title" className="max-w-md" z="z-[80]">
      <div className="p-6 pb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-5 text-red-500 bg-red-500/10 border border-red-500/20">
          <TriangleAlert className="size-5" />
        </div>
        <h2 id="hub-delete-title" className="text-xl font-bold mb-2">
          {t("tasks.hubs.delete.title", { defaultValue: "Delete {{name}}?", name: hubName })}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed mb-4">
          {t("tasks.hubs.delete.body", {
            defaultValue: "This deletes {{squads}} squads and {{tasks}} tasks. This can't be undone.",
            squads,
            tasks,
          })}
        </p>
        <label htmlFor="hub-delete-typed" className="text-sm font-semibold block mb-2">
          {t("tasks.hubs.delete.typeToConfirm", { defaultValue: "Type {{name}} to confirm", name: hubName })}
        </label>
        <input
          id="hub-delete-typed"
          ref={inputRef}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && ok && !busy && onConfirm()}
          className="w-full h-11 px-4 bg-transparent border border-border rounded-2xl text-[15px] outline-none focus:border-foreground/60 focus:ring-1 focus:ring-foreground/40"
        />
      </div>
      <div className="flex gap-3 p-4 pt-0">
        <button type="button" onClick={onClose} className="flex-1 py-3 px-4 rounded-2xl border border-border font-semibold text-sm hover:bg-muted transition-colors">
          {t("tasks.cancel", { defaultValue: "Cancel" })}
        </button>
        <button
          type="button"
          disabled={!ok || busy}
          onClick={onConfirm}
          className="flex-1 py-3 px-4 rounded-2xl font-semibold text-sm bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-40"
        >
          {t("tasks.hubs.menu.delete", { defaultValue: "Delete hub" })}
        </button>
      </div>
    </Modal>
  );
}

/** Right-hand side panel shell (US 1.4: Edit hub opens from the right). */
export function SidePanel({ children, onClose, labelledBy }: { children: ReactNode; onClose: () => void; labelledBy: string }) {
  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/40 backdrop-blur-[2px]" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className="w-full max-w-md h-full bg-background border-s border-border shadow-2xl flex flex-col anim-slide-in-right"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </aside>
    </div>
  );
}
